'use client';

import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSearchParams } from 'next/navigation';
import 'react-loading-skeleton/dist/skeleton.css';
import { MAX_BLOG_ENTRIES_PER_PAGE } from '@src/typescriptGlobals/constants';
import { BlogSummaryTypes } from '@src/typescriptGlobals/contentful.types';
import Dot from '@src/components/Icons/Dot';
import ArrowForward from '@src/components/Icons/ArrowForward';
import NextImage from '@src/components/Shared/NextImage';
import Container from '@src/components/Shared/Container';
import Button from '@src/components/Shared/Button';
import PostEntry from '@src/components/Shared/PostEntry';
import Pagination from '@src/components/Shared/Pagination';
import NextLink from '@src/components/Shared/NextLink';
import LanguageSwitcher from '@src/components/Shared/LanguageSwitcher';
import useEffectExceptOnMount from '@src/hooks/useEffectExceptOnMount';
import dateFormatter from '@src/utils/dateFormatter';
import isEmpty from '@src/utils/isEmpty';
import animations from './animations.module.css';
import useAnimateFeaturedPost from './useAnimateFeaturedPost';
import { NewsAndResearchPropTypes } from './NewsPage.types';
import { RootStateTypes, useTypedAppSelector, useTypedDispatch } from '@src/redux/store/store.types';
import { fetchLatestAndNewsDataAXN, fetchNewsDataAXN } from '@src/redux/actions/newsActions/newsActions';
import defaultToEnglishLocale from '@src/utils/defaultToEnglishLocale';
import urlWithLocale from '@src/utils/urlWithLocale';
const NewsPage: React.FC<NewsAndResearchPropTypes> = ({
  locale,
  pageName,
  globalTextBlock
}) => {
  const dispatch = useTypedDispatch();
  const {
    newsData,
    latestNewsData,
    isLoading
  } = useTypedAppSelector((state: RootStateTypes) => state.newsRXS);
  const queryParams = useSearchParams();
  const [searchParams, setQueryParams] = React.useState<{
    [key: string]: string;
  }>({});
  React.useEffect(() => {
    const params: {
      [key: string]: string;
    } = Object.fromEntries(queryParams.entries());
    setQueryParams(params);
  }, [queryParams]);
  const {
    featureImageRef
  } = useAnimateFeaturedPost({
    categorySlug: searchParams?.category
  });
  React.useEffect(() => {
    dispatch(fetchLatestAndNewsDataAXN({
      categorySlug: searchParams?.category === 'all' ? null : searchParams?.category,
      locale: defaultToEnglishLocale({
        locale
      })
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffectExceptOnMount(() => {
    dispatch(fetchNewsDataAXN({
      categorySlug: searchParams?.category === 'all' ? null : searchParams?.category,
      locale: defaultToEnglishLocale({
        locale
      })
    }));
  }, [searchParams?.category]);
  return <>
      <Container className='mt-[5rem]' data-sentry-element="Container" data-sentry-source-file="NewPage.client.tsx">
        <div className='mx-auto max-w-[140rem]'>
          <h1 className='mb-[1.6rem] text-[2.4rem] font-[500] text-body-copy--dark md:text-[3.2rem] lg:mb-[3.2rem] lg:text-[4.8rem]'>
            {pageName}
          </h1>

          {/* Featured blog component */}
          <div ref={featureImageRef}>
            {!isEmpty({
            value: newsData
          }) ? <div className={`${animations.fadeIn} grid grid-cols-1 lg:grid-cols-2 lg:gap-[3.6rem]`}>
                <NextLink href={urlWithLocale({
              locale,
              path: `/news/${latestNewsData?.slug}`
            })} aria-label={latestNewsData?.title ?? ''} className='mb-[1.6rem] md:mb-[2.4rem]'>
                  <NextImage className='h-auto w-full' priority src={latestNewsData?.thumbnail?.url} alt={latestNewsData?.thumbnail?.description ?? ''} width={latestNewsData?.thumbnail?.width} />
                </NextLink>

                <div className='flex-1  lg:mt-[0] 3xl:mt-[3rem] 3xl:w-[85%]'>
                  <NextLink href={urlWithLocale({
                locale,
                path: `/news/${latestNewsData?.slug}`
              })} className='text-header-copy mb-[0.6rem] inline-block text-[2rem] font-[500] leading-[2.5rem] md:mb-[1rem] md:text-[2.6rem] md:leading-[3.25rem] lg:text-[3.2rem] lg:leading-[3.6rem] xl:text-[4rem] xl:leading-[4.4rem] 3xl:mb-[2rem] ' aria-label={latestNewsData?.title ?? ''}>
                    {latestNewsData?.title}
                  </NextLink>
                  <div className='mb-[1rem] flex flex-wrap items-center gap-[0.8rem] text-[1.4rem]'>
                    <p className='font-normal text-body-copy--dark'>
                      {latestNewsData?.categoriesCollection?.items?.[0]?.title ?? ''}
                    </p>
                    <Dot />
                    <time dateTime={latestNewsData?.datePublished}>
                      {dateFormatter(latestNewsData?.datePublished)}
                    </time>
                  </div>

                  <div className='mb-[3rem] flex flex-wrap gap-[0.8rem]'>
                    <LanguageSwitcher post={latestNewsData} />
                  </div>

                  {latestNewsData?.excerpt ? <p className='text-header-copy mb-[1.8rem] flex text-[1.6rem] font-[300] leading-[2.2rem] md:leading-[2.4rem] lg:mb-[2.4rem] xl:mb-[3.2rem] xl:text-[1.8rem] xl:leading-[2.7rem]'>
                      {latestNewsData?.excerpt}
                    </p> : <div className='pb-[1.8rem]' />}

                  <Button tertiary href={urlWithLocale({
                locale,
                path: `/news/${latestNewsData?.slug}`
              })} btnText={globalTextBlock?.items?.[0]?.learnMore} align='justify-start' />
                </div>
              </div> : <div className='relative z-[-1] grid grid-cols-1 lg:grid-cols-2 lg:gap-[3.6rem]'>
                <Skeleton className='h-[30rem] lg:h-[60rem]' />
                <div>
                  <Skeleton className='mb-[1rem] mt-[2.4rem] h-[8rem]' />
                  <Skeleton className='mb-[1rem] h-[2rem] !w-[20%]' />
                  <Skeleton className='h-[30rem]' />
                </div>
              </div>}
          </div>

          <div className='relative'>
            <div className='no-scrollbar relative mt-[3.2rem] flex gap-[1.7rem] overflow-x-auto pr-[3rem] lg:gap-[2.4rem]'>
              {newsData?.postCategories?.items?.map?.(item => {
              return <React.Fragment key={`${item?.sys?.id}-category`}>
                    <NextLink replace ariaLabel={item?.slug} href={urlWithLocale({
                  locale,
                  path: item?.slug?.includes('all') ? '/news' : `/news/?${new URLSearchParams({
                    category: item?.slug
                  })}`
                })} className={`mb-[2.4rem] inline-block cursor-pointer text-center font-[400] text-body-copy--dark ${item?.slug === searchParams?.category || item?.slug?.includes('all') && isEmpty({
                  value: searchParams?.category
                }) ? 'border-b-[0.1rem] font-[500] text-primary' : ''}`}>
                      <span className='custom-before-text-shift-fix pb-4 inline-block h-full whitespace-nowrap text-[1.8rem] before:invisible before:block before:h-[0] before:overflow-hidden before:font-[700] before:content-[attr(title)] md:hover:font-[500] md:hover:text-primary lg:text-[2rem]' title={item.title}>
                        {item?.title}
                      </span>
                    </NextLink>
                    <div className='absolute mt-[2.4rem] h-[0.16rem] bg-primary lg:mt-[3rem] lg:h-[0.2rem]' />
                  </React.Fragment>;
            })}
            </div>
            <div className='absolute right-[-0.2rem] top-[-1rem] ml-auto h-[4rem] w-[5rem] bg-gradient-to-r from-[transparent] via-[#fff] to-[#fff] xl:hidden'></div>
            <ArrowForward className=' z-1000 absolute right-[0] top-[0.5rem] h-[1.3rem] w-[1.3rem] text-body-copy--dark xl:hidden' data-sentry-element="ArrowForward" data-sentry-source-file="NewPage.client.tsx" />
          </div>

          {!isLoading && !isEmpty({
          value: newsData
        }) ? <div className={`${animations.fadeIn} relative z-[10] bg-background-secondary`}>
              {!isEmpty({
            value: newsData?.blogs?.items
          }) && !isLoading ? <div className='grid grid-cols-1 gap-x-[1.7rem] md:grid-cols-2 xl:grid-cols-3 2xl:gap-x-[8rem]'>
                  {newsData?.blogs?.items?.map((post: BlogSummaryTypes) => <PostEntry key={post?.sys?.id} blog={post} searchParams={searchParams} globalTextBlock={globalTextBlock} locale={locale} />)}
                </div> : <>
                  {isEmpty({
              value: newsData?.blogs?.items
            }) && <div>{globalTextBlock?.items?.[0]?.noDataFound}</div>}
                </>}

              {!isEmpty({
            value: newsData?.blogs?.items
          }) && <Pagination totalNumberOfPages={Math.ceil(newsData?.blogs?.total / MAX_BLOG_ENTRIES_PER_PAGE)} currentPageFromProps={1} />}
            </div> : <div className={`grid grid-cols-1 gap-[3.2rem] lg:grid-cols-3`}>
              <Skeleton className='h-[30rem] w-[33.33%]' />
              <Skeleton className='h-[30rem] w-[33.33%]' />
              <Skeleton className='h-[30rem] w-[33.33%]' />
            </div>}
        </div>
      </Container>
    </>;
};
export default NewsPage;